import { TEAMSNAP_URL, APP_URL } from '../../core/constants';
import SearchIndex from './search-index';
import Tokenizers from './tokenizers';

const cacheTime = 60 * 1 * 1000;
const activeUseTime = 10 * 1000;

export enum SearchResultType {
  ClubSeason = 'club_season',
  OrganizationMember = 'organization_member',
  SeasonParticipant = 'season_participant',
  SeasonDivision = 'season_division',
  SeasonTeam = 'season_team',
  StandaloneTeam = 'team',
  StandaloneTeamMember = 'team_member',
  Reg1Form = 'reg_1_form',
  Invoice = 'invoice',
}

export interface LinkInfo {
  seasonRootDivisionId?: number;
  organizationId?: number;
  memberId?: number;
  programId?: number;
  seasonId?: number;
  divisionId?: number;
  teamId?: number;
  registrationFormId?: number;
  invoiceId?;
}

export interface SearchItem {
  type: string;
  text: string;
  subText?: string;
  linkInfo: LinkInfo;
  subLinks?: SearchItem[];
}

const suggestionExtraInfo = suggestion => {
  let suggestionInfo = null;

  switch (suggestion.type) {
    case SearchResultType.ClubSeason:
      suggestionInfo = {
        linkUrl: `/${suggestion?.linkInfo?.seasonRootDivisionId}/division_teams`,
        linkHost: TEAMSNAP_URL,
        isReactRoute: false,
        defaultIcon: 'divisions',
      };
      break;
    case SearchResultType.OrganizationMember:
      suggestionInfo = {
        linkUrl: `/organizations/${suggestion?.linkInfo?.organizationId}/members/${suggestion?.linkInfo?.memberId}`,
        linkHost: APP_URL,
        isReactRoute: true,
        defaultIcon: 'user',
      };
      break;
    case SearchResultType.SeasonParticipant:
      suggestionInfo = {
        linkUrl: `/organizations/${suggestion?.linkInfo?.organizationId}/programs/${suggestion?.linkInfo?.programId}/seasons/${suggestion?.linkInfo?.seasonId}/participants/${suggestion?.linkInfo?.memberId}`,
        linkHost: APP_URL,
        isReactRoute: true,
        defaultIcon: 'user',
      };
      break;
    case SearchResultType.SeasonDivision:
      suggestionInfo = {
        linkUrl: `/${suggestion?.linkInfo?.divisionId}/division_teams`,
        linkHost: TEAMSNAP_URL,
        isReactRoute: false,
        defaultIcon: 'divisions',
      };
      break;
    case SearchResultType.SeasonTeam:
      suggestionInfo = {
        linkUrl: `/${suggestion?.linkInfo?.teamId}`,
        linkHost: TEAMSNAP_URL,
        isReactRoute: false,
        defaultIcon: 'roster',
      };
      break;
    case SearchResultType.StandaloneTeam:
      suggestionInfo = {
        linkUrl: `/${suggestion?.linkInfo?.teamId}`,
        linkHost: TEAMSNAP_URL,
        isReactRoute: false,
        defaultIcon: 'roster',
      };
      break;
    case SearchResultType.StandaloneTeamMember:
      suggestionInfo = {
        linkUrl: `/${suggestion?.linkInfo?.teamId}/roster/${suggestion?.linkInfo?.member_id}`,
        linkHost: TEAMSNAP_URL,
        isReactRoute: false,
        defaultIcon: 'user',
      };
      break;
    case SearchResultType.Reg1Form:
      suggestionInfo = {
        linkUrl: `/divisions/${suggestion?.linkInfo?.seasonRootDivisionId}/registration/${suggestion?.linkInfo?.registrationFormId}`,
        linkHost: TEAMSNAP_URL,
        isReactRoute: false,
        defaultIcon: 'doc',
      };
      break;
    case SearchResultType.Invoice:
      suggestionInfo = {
        linkUrl: `/${suggestion?.linkInfo?.seasonRootDivisionId}/league_payments/invoicing/${suggestion?.linkInfo?.invoiceId}/invoices`,
        linkHost: TEAMSNAP_URL,
        isReactRoute: false,
        defaultIcon: 'credit-card',
      };
      break;
    default:
      break;
  }

  if (suggestionInfo) {
    suggestionInfo.linkUrl = suggestionInfo.linkUrl + '?utm_source=universal_search';
  }

  return suggestionInfo;
};

const createSearchIndex = () => new SearchIndex({
  datumTokenizer: Tokenizers.obj.whitespace(['text', 'subtext']),
  queryTokenizer: Tokenizers.whitespace,
});

const MINIMUM_SEARCH_LENGTH = 3;

export function getSuggestions(resultsCache: object, query: string, searchContext: object) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_dataset, _, _cacheKey, searchIndex] = getDataFromCache(resultsCache, query);

  // Need to use sorting by context
  const searchResults = searchIndex?.search(query) || [];

  return resultsForContext(searchResults, query, searchContext);
}

function resultsForContext(searchResults, query, searchContext) {
  const updatedResults  =  updateLinksByContext(searchResults, searchContext);

  return sortByContext(updatedResults, query, searchContext);
}

function updateLinksByContext(searchResults: any[], context) {
  // member vs participant links
  return searchResults.map(searchResult => {
    let contextMatch;

    if (searchResult.type === SearchResultType.OrganizationMember) {
      contextMatch = searchResult.subLinks.find((subLink) =>
        (context['seasonId'] && subLink.linkInfo['seasonId'] === context['seasonId']) ||
        (context['seasonRootDivisionId'] && subLink.linkInfo['seasonRootDivisionId'] === context['seasonRootDivisionId'])
      );
    }

    return contextMatch ? contextMatch : searchResult;
  });
}

function sortByContext(searchResults: any[], query: string, context) {
  return searchResults.sort((a, b) => {
    const aLocation = locationInMainText(a, query);
    const bLocation = locationInMainText(b, query);

    if (aLocation < bLocation) {
      return -1;
    }

    if (aLocation > bLocation) {
      return 1;
    }

    const aContextMatches = numberContextMatches(a, context);
    const bContextMatches = numberContextMatches(b, context);

    if (aContextMatches > bContextMatches) {
      return -1;
    }

    if (aContextMatches < bContextMatches) {
        return 1;
    }

    return 0;
  });
}

function locationInMainText(searchResult, query) {
  const firstToken = query.split(' ')[0].toLowerCase();
  const index = searchResult.text.toLowerCase().indexOf(firstToken);

  // Use index to sort by start of first word or 0 for just being in the text
  return index > -1 ? 0 : 99999;
}

function numberContextMatches(searchResult: any, context: any) {
  if (context) {
    return Object.entries(context).reduce((count, [key, value]) => {
      if (searchResult?.linkInfo[key] === value ) {
        count = count + 1;
      }
      return count;
    }, 0);
  } else {
    return 0;
  }
}

export function queryValidForSearch(query: string) {
  query = query.trim();
  return query.length >= MINIMUM_SEARCH_LENGTH;
}

export function getDataFromCache(resultsCache: object, query: string): [any[], object, string, any] {
  query = query.trim();
  const bestKey = possibleCacheKeys(query).reverse().find((string) => string in resultsCache);
  const cacheValue = resultsCache[bestKey];
  if (cacheValue) {
    cacheValue.lastAccessed = Date.now();
  }

  return [cacheValue?.results, resultsCache, bestKey, cacheValue?.searchIndex]
}

export function addResultsToCache(resultsCache: object, query: string, navigationResults: SearchItem[]) {
  query = query.trim();
  const navSearchResults = navigationResults.map((searchItem) => {
    if (searchItem.subLinks?.length > 0) {
      const subLinks = searchItem.subLinks.map((subLink) => {
        const subExtra = suggestionExtraInfo(subLink)
        return { ...subLink, ...subExtra };
      });

      searchItem.subLinks = subLinks;
    }
    const extraInfo = suggestionExtraInfo(searchItem);

    return { ...searchItem, ...extraInfo };
  });

  const searchIndex = createSearchIndex();
  searchIndex.add(navSearchResults, query);

  resultsCache[query] = {
    key: query,
    results: navSearchResults,
    createdAt: Date.now(),
    lastAccessed: Date.now(),
    searchIndex: searchIndex,
  }

  return resultsCache;
}

export function cleanUpCache(resultsCache: object) {
  const updatedCache = Object.keys(resultsCache).reduce((updatedCache, key)  => {
    const value = resultsCache[key];

    if (value.lastAccessed > (Date.now() - activeUseTime)
      || value.createdAt >  (Date.now() - cacheTime)) {
      updatedCache[key] = value;
    }

    return updatedCache;
  }, {});

  return updatedCache;
}

function possibleCacheKeys(query: string) {
  const start = MINIMUM_SEARCH_LENGTH - 1;
  const end = query.length - 1;

  return Array.from({ length:end + 1 - start }, (v, k) => start + 1 + k).map( (length) =>
    query.substr(0, length)
  ) ;
}
